import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError, api, unwrap } from '@/lib/api'
import { idempotencyKey } from '@/lib/utils'
import type { components } from '@/api/schema'

/**
 * Billing area: plans, checkout (Razorpay), credits, invoices, billing
 * details, usage and budgets. Prices come from the API's catalogue, never
 * from the console, so the two can't disagree.
 */

type S = components['schemas']
export type Catalogue = S['BillingCatalogue']
export type BillingPlan = S['BillingPlan']
export type BillingSummary = S['BillingSummary']
export type BillingOverview = S['BillingOverview']
export type BillingSubscription = S['BillingSubscription']
export type BillingQuote = S['BillingQuote']
export type CheckoutOptions = S['BillingCheckoutOptions']
export type CheckoutStatus = S['BillingCheckoutStatus']
export type BillingCredits = S['BillingCredits']
export type CreditEntry = S['BillingCreditEntry']
export type InvoiceSummary = S['BillingInvoiceSummary']
export type Invoice = S['BillingInvoice']
export type BillingDetails = S['BillingDetails']
export type BillingDetailsView = S['BillingDetailsView']
export type Budget = S['BillingBudget']
export type BudgetList = S['BillingBudgetList']
export type Entitlements = S['BillingEntitlements']
export type Usage = S['BillingUsage']
export type PaymentMethod = S['BillingPaymentMethod']
export type CancelPreview = S['BillingCancelPreview']
export type PlanId = BillingPlan['id']
export type Interval = 'month' | 'year'

export const catalogueQuery = () => queryOptions({ queryKey: ['billing', 'catalogue'] as const, queryFn: () => unwrap(api.GET('/esm/v1/billing/catalogue')), staleTime: 10 * 60_000 })
export const summaryQuery = (orgId: string) => queryOptions({ queryKey: ['billing', orgId, 'summary'] as const, queryFn: () => unwrap(api.GET('/esm/v1/billing/summary')), staleTime: 60_000, retry: false })
export const overviewQuery = (orgId: string) => queryOptions({ queryKey: ['billing', orgId, 'overview'] as const, queryFn: () => unwrap(api.GET('/esm/v1/billing/overview')) })
export const entitlementsQuery = (orgId: string) => queryOptions({ queryKey: ['billing', orgId, 'entitlements'] as const, queryFn: () => unwrap(api.GET('/esm/v1/billing/entitlements')) })
export const creditsQuery = (orgId: string) => queryOptions({ queryKey: ['billing', orgId, 'credits'] as const, queryFn: () => unwrap(api.GET('/esm/v1/billing/credits')) })
export const creditHistoryQuery = (orgId: string) => queryOptions({ queryKey: ['billing', orgId, 'credits', 'history'] as const, queryFn: () => unwrap(api.GET('/esm/v1/billing/credits/history', { params: { query: { limit: 200 } } })) })
export const invoicesQuery = (orgId: string) => queryOptions({ queryKey: ['billing', orgId, 'invoices'] as const, queryFn: () => unwrap(api.GET('/esm/v1/billing/invoices', { params: { query: { limit: 200 } } })) })
export const invoiceQuery = (orgId: string, id: string) =>
  queryOptions({ queryKey: ['billing', orgId, 'invoices', id] as const, queryFn: () => unwrap(api.GET('/esm/v1/billing/invoices/{invoice_id}', { params: { path: { invoice_id: id } } })), retry: false })
export const detailsQuery = (orgId: string) => queryOptions({ queryKey: ['billing', orgId, 'details'] as const, queryFn: () => unwrap(api.GET('/esm/v1/billing/details')) })
export const budgetsQuery = (orgId: string) => queryOptions({ queryKey: ['billing', orgId, 'budgets'] as const, queryFn: () => unwrap(api.GET('/esm/v1/billing/budgets')) })
export const paymentMethodQuery = (orgId: string) => queryOptions({ queryKey: ['billing', orgId, 'payment-method'] as const, queryFn: () => unwrap(api.GET('/esm/v1/billing/payment-method')) })
export const cancelPreviewQuery = (orgId: string) => queryOptions({ queryKey: ['billing', orgId, 'cancel-preview'] as const, queryFn: () => unwrap(api.GET('/esm/v1/billing/subscription/cancel-preview')) })
export const subscriptionsQuery = (orgId: string) => queryOptions({ queryKey: ['billing', orgId, 'subscriptions'] as const, queryFn: () => unwrap(api.GET('/esm/v1/billing/subscriptions')) })

export const usageQuery = (orgId: string, from: string, to: string) =>
  queryOptions({ queryKey: ['billing', orgId, 'usage', from, to] as const, queryFn: () => unwrap(api.GET('/esm/v1/billing/usage', { params: { query: { from, to } } })), placeholderData: keepPreviousData })

export const quoteQuery = (orgId: string, q: { plan_id: PlanId; interval: Interval; seats?: number }) =>
  queryOptions({
    queryKey: ['billing', orgId, 'quote', q.plan_id, q.interval, q.seats ?? 0] as const,
    queryFn: () => unwrap(api.GET('/esm/v1/billing/quote', { params: { query: { plan_id: q.plan_id, interval: q.interval, ...(q.seats ? { seats: q.seats } : {}) } } })),
    retry: false,
    placeholderData: keepPreviousData,
  })

/* ─────────────────────────── checkout ─────────────────────────── */

export const startCheckout = (body: { plan_id: PlanId; interval: Interval; seats?: number }, key = idempotencyKey()) =>
  unwrap(api.POST('/esm/v1/billing/checkout', { body, headers: { 'Idempotency-Key': key } }))
export const startTopUp = (pack_paise: number, key = idempotencyKey()) => unwrap(api.POST('/esm/v1/billing/credits/topup', { body: { pack_paise }, headers: { 'Idempotency-Key': key } }))
export const startResume = (key = idempotencyKey()) => unwrap(api.POST('/esm/v1/billing/subscription/resume', { headers: { 'Idempotency-Key': key } }))
export const startMethodChange = (key = idempotencyKey()) => unwrap(api.POST('/esm/v1/billing/payment-method/change', { headers: { 'Idempotency-Key': key } }))
export const verifyPayment = (body: { razorpay_payment_id: string; razorpay_signature: string; razorpay_subscription_id?: string; razorpay_order_id?: string }) =>
  unwrap(api.POST('/esm/v1/billing/checkout/verify', { body }))
export const checkoutStatus = (id: string) => unwrap(api.GET('/esm/v1/billing/checkouts/{checkout_id}', { params: { path: { checkout_id: id } } }))
export const abandonCheckout = (id: string) => unwrap(api.POST('/esm/v1/billing/checkouts/{checkout_id}/abandon', { params: { path: { checkout_id: id } } }))

/* ─────────────────────────── mutations ─────────────────────────── */

function useBillingMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing'] })
      void qc.invalidateQueries({ queryKey: ['session'] })
    },
  })
}

export type DetailsInput = { legal_name: string; address_line1: string; address_line2?: string; city: string; state_code: string; pin: string; gstin?: string; pan?: string; emails: string[]; po_number?: string }
export const useSaveDetails = () => useBillingMutation((body: DetailsInput) => unwrap(api.PUT('/esm/v1/billing/details', { body })))

export type BudgetInput = { name: string; scope: { kind: 'workspace' | 'key'; id: string }; monthly_limit_credits: number; thresholds: number[]; action: 'alert' | 'block' }
export const useCreateBudget = () => useBillingMutation((body: BudgetInput) => unwrap(api.POST('/esm/v1/billing/budgets', { body, headers: { 'Idempotency-Key': idempotencyKey() } })))
export const useUpdateBudget = () =>
  useBillingMutation(({ id, ...body }: { id: string } & Partial<Omit<BudgetInput, 'scope'>>) => unwrap(api.PATCH('/esm/v1/billing/budgets/{budget_id}', { params: { path: { budget_id: id } }, body })))
export const useDeleteBudget = () => useBillingMutation((id: string) => unwrap(api.DELETE('/esm/v1/billing/budgets/{budget_id}', { params: { path: { budget_id: id } } })))

export type CancelInput = { when: 'period_end' | 'now'; reason?: 'too_expensive' | 'missing_feature' | 'switching' | 'one_project' | 'other'; comment?: string }
export const cancelPlan = (body: CancelInput) => unwrap(api.POST('/esm/v1/billing/subscription/cancel', { body }))
export const useCancelScheduled = () => useBillingMutation(() => unwrap(api.POST('/esm/v1/billing/subscription/scheduled/cancel')))

/* ─────────────────────────── downloads ─────────────────────────── */

/** Authenticated download (the session cookie goes with it); saves under `filename`. */
export async function download(path: string, filename: string): Promise<void> {
  let res: Response
  try {
    res = await fetch(path, { credentials: 'include' })
  } catch {
    throw new ApiError(0, 'NETWORK', 'Can’t reach ESM. Check your connection and try again.')
  }
  if (!res.ok) {
    let msg = 'The download didn’t work. Try again.'
    try {
      msg = ((await res.json()) as { error?: { message?: string } }).error?.message ?? msg
    } catch {
      /* not JSON */
    }
    throw new ApiError(res.status, `HTTP_${res.status}`, msg)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
export const invoicePdfPath = (id: string) => `/esm/v1/billing/invoices/${encodeURIComponent(id)}/pdf`
export const usageCsvPath = (from: string, to: string) => `/esm/v1/billing/usage/export?from=${from}&to=${to}`
